import { detectBrand } from './detectBrand';
import { PciVaultProvider } from './providers/PciVault';
import { createProviders } from './providers/factory';
import { authenticate3ds } from './threeds';
import type {
  AuthenticateInput,
  AuthenticateResult,
  CapturePayload,
  CardInput,
  IVaultProvider,
  ProviderConfig,
  ThreeDsPreflightResponse,
  TokenPayload,
  TokenResult,
  TokenizerConfigResponse,
  VaultPrepareResponse,
  VaultTokenPayload,
  Z2PayConfig,
} from './types';

declare const process: { env: { API_ENDPOINT: string } };

const DEFAULT_TIMEOUT = 15000;
const RETRY_DELAY = 1000;

export class Z2Pay {
  private readonly apiUrl: string;
  private readonly companyId: string;
  private readonly timeout: number;
  private configCache: TokenizerConfigResponse | null = null;

  constructor(config: Z2PayConfig) {
    this.companyId = config.companyId;
    this.timeout = config.timeout ?? DEFAULT_TIMEOUT;
    this.apiUrl = config.apiUrl ?? process.env.API_ENDPOINT;
  }

  /**
   * Tokeniza cartão em paralelo (PSPs + vaults), envia pro backend, retorna tokenId.
   */
  async createCardToken(card: CardInput): Promise<TokenResult> {
    // 1. Busca config dos PSPs (com cache)
    const configs = await this.loadConfig();

    // 2. Tokeniza em paralelo: PSPs + vaults
    const [pspTokens, vaultTokens] = await Promise.all([
      this.tokenizeWithPsps(card, configs),
      this.tokenizeWithVaults(card),
    ]);

    if (pspTokens.length === 0 && vaultTokens.length === 0) {
      throw new Error('Failed to generate any tokens');
    }

    // 3. Monta payload de card info
    const [month, year] = card.expiration.split('/');
    const info = {
      firstDigits: card.number.substring(0, 6),
      lastDigits: card.number.slice(-4),
      holderName: card.holder,
      expirationMonth: month.padStart(2, '0'),
      expirationYear: year.length === 2 ? `20${year}` : year,
      brand: detectBrand(card.number),
    };

    // 4. POST /tokenizer/capture → salva no Redis, retorna tokenId
    const payload: CapturePayload = {
      tokens: pspTokens,
      info,
    };
    if (vaultTokens.length > 0) {
      payload.vaultTokens = vaultTokens;
    }

    const response = await this.request<{ tokenId: string }>(
      `/tokenizer/${this.companyId}/capture`,
      {
        method: 'POST',
        body: payload,
      },
    );

    return { tokenId: response.tokenId };
  }

  /**
   * Autenticação 3DS browser-first: pré-flight → desafio (renderizado em
   * `input.container`) → comprovante pra enviar em `creditCard.authentication`
   * no `POST /transactions`. NUNCA lança: falha de rede, script ou timeout
   * vira `{ status: 'bypassed' }` — 3DS indisponível não mata a venda.
   */
  async authenticate(input: AuthenticateInput): Promise<AuthenticateResult> {
    return authenticate3ds(input, () =>
      this.request<ThreeDsPreflightResponse>(`/threeds/${this.companyId}/preflight`, {
        method: 'POST',
        body: {
          amount: input.amount,
          currency: input.currency,
          installments: input.installments,
        },
      }),
    );
  }

  // ─── Private ────────────────────────────────────────────────

  private async loadConfig(): Promise<ProviderConfig[]> {
    if (this.configCache) return this.configCache.providers;

    const response = await this.request<TokenizerConfigResponse>(
      `/tokenizer/${this.companyId}/config`,
    );
    this.configCache = response;

    return response.providers;
  }

  /**
   * Para cada vault provider ativo, chama prepare → cria PciVaultProvider one-time → capture.
   */
  private async tokenizeWithVaults(card: CardInput): Promise<VaultTokenPayload[]> {
    const config = this.configCache;
    if (!config?.vaultProviders?.length) return [];

    const promises = config.vaultProviders.map(async (vaultConfig) => {
      try {
        // Step 1: prepare — cria capture endpoint one-time
        const session = await this.request<VaultPrepareResponse>(
          `/tokenizer/${vaultConfig.id}/vault/prepare`,
          { method: 'POST' },
        );

        if (!session?.captureUrl || !session?.captureSecret) return null;

        // Step 2: capture card no vault
        const vaultProvider = this.createVaultProvider(
          vaultConfig.provider,
          session.captureUrl,
          session.captureSecret,
        );
        const result = await vaultProvider.capture(card);

        return {
          provider: vaultConfig.provider,
          token: result.token,
        } as VaultTokenPayload;
      } catch {
        return null;
      }
    });

    const results = await Promise.allSettled(promises);

    return results
      .filter(
        (r): r is PromiseFulfilledResult<VaultTokenPayload | null> => r.status === 'fulfilled',
      )
      .map((r) => r.value)
      .filter((v): v is VaultTokenPayload => v !== null);
  }

  private createVaultProvider(
    provider: string,
    captureUrl: string,
    captureSecret: string,
  ): IVaultProvider {
    if (provider === 'pcivault') {
      return new PciVaultProvider(captureUrl, captureSecret);
    }
    throw new Error(`Unknown vault provider: ${provider}`);
  }

  private async tokenizeWithPsps(
    card: CardInput,
    configs: ProviderConfig[],
  ): Promise<TokenPayload[]> {
    const providers = createProviders(configs);
    if (providers.length === 0) return [];

    const promises = providers.map(async ({ provider, config }) => {
      try {
        const token = await this.withRetry(() => provider.generate(card));
        return {
          provider: config.provider,
          gatewayConfigId: config.gatewayConfigId,
          token,
        } as TokenPayload;
      } catch {
        return null;
      }
    });

    const results = await this.withTimeout(Promise.allSettled(promises));

    return results
      .filter((r): r is PromiseFulfilledResult<TokenPayload | null> => r.status === 'fulfilled')
      .map((r) => r.value)
      .filter((v): v is TokenPayload => v !== null);
  }

  private async withRetry<T>(fn: () => Promise<T>, retries = 1): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (retries <= 0) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return this.withRetry(fn, retries - 1);
    }
  }

  private async withTimeout<T>(promise: Promise<T>): Promise<T> {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), this.timeout),
      ),
    ]);
  }

  private async request<T>(
    path: string,
    options?: { method?: string; body?: unknown },
  ): Promise<T> {
    const url = `${this.apiUrl}${path}`;
    const headers: Record<string, string> = {};

    const init: RequestInit = {
      method: options?.method ?? 'GET',
      headers,
    };

    if (options?.body) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, init);

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Z2Pay API error (${response.status}): ${text}`);
    }

    return response.json() as Promise<T>;
  }
}
